import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { get } from '../../api';
import styles from './AdminPortalTmaReportSummary.module.scss';

// ── Geo helpers ───────────────────────────────────────────────────────────────
function haversineMiles(lat1, lon1, lat2, lon2) {
    const R = 3958.8;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calcDayMiles(points) {
    let miles = 0;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        if (a.latitude && a.longitude && b.latitude && b.longitude) {
            miles += haversineMiles(a.latitude, a.longitude, b.latitude, b.longitude);
        }
    }
    return miles;
}

/** Centroid of valid lat/lng points. */
function routeCentroid(points) {
    let sumLat = 0;
    let sumLng = 0;
    let n = 0;
    for (const p of points) {
        if (!p.latitude || !p.longitude) continue;
        sumLat += Number(p.latitude);
        sumLng += Number(p.longitude);
        n += 1;
    }
    if (!n) return null;
    return { lat: sumLat / n, lng: sumLng / n };
}

function maxRadiusFromCenterMiles(points, cLat, cLng) {
    let r = 0;
    for (const p of points) {
        if (!p.latitude || !p.longitude) continue;
        const d = haversineMiles(cLat, cLng, p.latitude, p.longitude);
        if (d > r) r = d;
    }
    return r;
}

function isDeployedPoint(p) {
    return p.deployment_status === 0 || p.deployment_status === 'deployed' || p.deployment_status === '0';
}

function isHalfDeployedPoint(p) {
    return p.deployment_status === 4 || p.deployment_status === 'half_deployed' || p.deployment_status === '4';
}

/**
 * When points stay inside a small disk but the reported path length is much larger,
 * treat as stationary GPS drift and prefer a single radius on the map.
 */
function detectGpsDriftCluster(points) {
    const valid = points.filter(p => p.latitude && p.longitude);
    const MIN_POINTS = 5;
    if (valid.length < MIN_POINTS) return null;

    const center = routeCentroid(valid);
    if (!center) return null;

    const rMaxMi = maxRadiusFromCenterMiles(valid, center.lat, center.lng);
    const diameterMi = 2 * rMaxMi;
    const pathMi = calcDayMiles(valid);
    const pathOverDiameter = pathMi / Math.max(diameterMi, 0.0005);

    const SPAN_LOOSE_MI = 0.2;
    const SPAN_TIGHT_MI = 0.09;
    const PATH_RATIO_LOOSE = 1.75;

    const looseDrift =
        diameterMi <= SPAN_LOOSE_MI && pathOverDiameter >= PATH_RATIO_LOOSE;
    const tightCluster = diameterMi <= SPAN_TIGHT_MI;

    if (!looseDrift && !tightCluster) return null;

    let stroke = '#6b7280';
    const dep = valid.filter(isDeployedPoint).length;
    const half = valid.filter(isHalfDeployedPoint).length;
    if (dep >= half && dep >= valid.length / 2) stroke = '#10b981';
    else if (half > 0) stroke = '#f59e0b';

    return {
        center,
        radiusMiles: rMaxMi,
        pathMi,
        diameterMi,
        stroke,
        samplePoint: valid[Math.floor(valid.length / 2)],
    };
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function getDaysInRange(fromDate, toDate) {
    const days = [];
    const cur = new Date(fromDate + 'T12:00:00');
    const end = new Date(toDate + 'T12:00:00');
    while (cur <= end) {
        const y = cur.getFullYear();
        const m = String(cur.getMonth() + 1).padStart(2, '0');
        const d = String(cur.getDate()).padStart(2, '0');
        days.push(`${y}-${m}-${d}`);
        cur.setDate(cur.getDate() + 1);
    }
    return days;
}

function parseAspDate(val) {
    if (!val) return null;
    const m = /\/Date\((\d+)([+-]\d+)?\)\//.exec(val);
    return m ? new Date(Number(m[1])) : new Date(val);
}

function isWorking(device) {
    const updated = parseAspDate(device.status_updated_time);
    if (!updated || isNaN(updated.getTime())) return false;
    return (Date.now() - updated.getTime()) < 24 * 60 * 60 * 1000;
}

function fmtMiles(m) {
    return m < 0.05 ? '0 mi' : `${m.toFixed(1)} mi`;
}

function fmtDateLabel(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
    });
}

function fmtWeekday(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short' });
}

function fmtDateShort(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const TZ_OFFSET = new Date().getTimezoneOffset();

function deviceCardTitles(device) {
    const asset =
        device.report_asset_name ||
        device.asset_name ||
        null;
    const serial = device.serial_number || null;
    const devName = device.name || null;
    if (asset) {
        const secondary =
            serial ||
            (devName && devName !== asset ? devName : null) ||
            `Device #${device.id}`;
        return { primary: asset, secondary };
    }
    const primary = devName || serial || `Device #${device.id}`;
    const secondary =
        devName && serial && primary === devName
            ? serial
            : devName && serial && primary === serial
                ? devName
                : null;
    return { primary, secondary };
}

function deviceMapLabel(device) {
    if (!device) return '';
    const { primary, secondary } = deviceCardTitles(device);
    return secondary && secondary !== primary
        ? `${primary} (${secondary})`
        : primary;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AdminPortalTmaReportSummary({ customer, devices, fromDate, toDate, onBack }) {
    const days = getDaysInRange(fromDate, toDate);

    // per-device stats: { [id]: { dailyMiles: [{date,miles}], totalMiles, loading } }
    const [deviceStats, setDeviceStats] = useState(() => {
        const init = {};
        devices.forEach(d => { init[d.id] = { dailyMiles: [], totalMiles: 0, loading: true }; });
        return init;
    });

    const [focusedDeviceId, setFocusedDeviceId] = useState(devices[0]?.id ?? null);
    const [focusedDay, setFocusedDay] = useState(null);
    const [dayRouteLoading, setDayRouteLoading] = useState(false);
    /** True when the day's points were drawn as a drift radius instead of a polyline path. */
    const [dayRouteIsGpsDrift, setDayRouteIsGpsDrift] = useState(false);

    // map refs
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const polylineRef = useRef(null);
    const routeMarkersRef = useRef([]);
    const routeCirclesRef = useRef([]);
    const deviceMarkerRef = useRef(null);
    const mapReadyRef = useRef(false);

    // ── Load mileage for every device / day (bulk endpoint) ──────────────
    useEffect(() => {
        if (!devices.length) return;

        const deviceIds = devices.map(d => d.id);

        // Build query params: device_ids[] for each id
        const params = new URLSearchParams();
        deviceIds.forEach(id => params.append('device_ids[]', id));
        params.set('start_date', fromDate);
        params.set('end_date', toDate);
        params.set('timezoneOffset', TZ_OFFSET);

        const endpoint = customer?.id ? `/customer/${customer.id}/history` : `/customer/history`;
        get(`${endpoint}?${params.toString()}`)
            .then(results => {
                // results: [{ device_id, history: DeviceLocation[] }]
                const arr = Array.isArray(results) ? results : [];

                // Build a lookup: device_id → history points
                const byDevice = {};
                arr.forEach(({ device_id, history }) => {
                    byDevice[device_id] = Array.isArray(history) ? history : [];
                });

                setDeviceStats(prev => {
                    const next = { ...prev };
                    devices.forEach(device => {
                        const pts = byDevice[device.id] || [];

                        // Group points by date (YYYY-MM-DD in the timezone-adjusted field)
                        const byDay = {};
                        days.forEach(d => { byDay[d] = []; });
                        pts.forEach(p => {
                            if (!p.latitude || !p.longitude) return;
                            // created_time is ASP /Date(milliseconds-offset)/ format
                            const dt = parseAspDate(p.created_time);
                            if (!dt || isNaN(dt.getTime())) return;
                            const y = dt.getFullYear();
                            const m = String(dt.getMonth() + 1).padStart(2, '0');
                            const d = String(dt.getDate()).padStart(2, '0');
                            const key = `${y}-${m}-${d}`;
                            if (key in byDay) byDay[key].push(p);
                        });

                        const dailyMiles = days.map(date => ({
                            date,
                            miles: calcDayMiles(byDay[date]),
                            hasLocations: byDay[date].length > 0,
                        }));
                        const totalMiles = dailyMiles.reduce((s, r) => s + r.miles, 0);

                        next[device.id] = { dailyMiles, totalMiles, loading: false };
                    });
                    return next;
                });
            })
            .catch(() => {
                // On error mark all devices as loaded with zero miles
                setDeviceStats(prev => {
                    const next = { ...prev };
                    devices.forEach(device => {
                        next[device.id] = {
                            dailyMiles: days.map(date => ({ date, miles: 0, hasLocations: false })),
                            totalMiles: 0,
                            loading: false,
                        };
                    });
                    return next;
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Initialize Google Map ────────────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current || !window.google) return;
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            zoom: 5,
            center: { lat: 39.5, lng: -98.35 },
            mapTypeId: 'roadmap',
            zoomControl: true,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
        });
        mapReadyRef.current = true;

        // show initial device position
        const d = devices.find(x => x.id === focusedDeviceId);
        if (d?.latitude && d?.longitude) {
            placeDevicePin(d);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── React to focusedDeviceId / focusedDay changes ────────────────────
    useEffect(() => {
        if (!mapReadyRef.current) return;
        clearOverlays();
        setDayRouteIsGpsDrift(false);

        if (focusedDay) {
            const device = devices.find(d => d.id === focusedDeviceId);
            if (!device) return;
            setDayRouteLoading(true);
            get(`/devices/${device.id}/history_date`, { date: focusedDay, timezoneOffset: TZ_OFFSET })
                .then(pts => {
                    const valid = Array.isArray(pts)
                        ? pts.filter(p => p.latitude && p.longitude)
                        : [];
                    drawRoute(valid);
                })
                .catch(() => { setDayRouteIsGpsDrift(false); })
                .finally(() => setDayRouteLoading(false));
        } else {
            const device = devices.find(d => d.id === focusedDeviceId);
            if (device) placeDevicePin(device);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedDeviceId, focusedDay]);

    function clearOverlays() {
        routeMarkersRef.current.forEach(m => m.setMap(null));
        routeMarkersRef.current = [];
        if (polylineRef.current) { polylineRef.current.setMap(null); polylineRef.current = null; }
        if (window.reportPolylines) {
            window.reportPolylines.forEach(p => p.setMap(null));
            window.reportPolylines = [];
        }
        routeCirclesRef.current.forEach(c => c.setMap(null));
        routeCirclesRef.current = [];
        if (deviceMarkerRef.current) { deviceMarkerRef.current.setMap(null); deviceMarkerRef.current = null; }
        if (activeInfoWindowRef.current) { activeInfoWindowRef.current.close(); activeInfoWindowRef.current = null; }
    }

    function placeDevicePin(device) {
        const map = mapInstanceRef.current;
        if (!map || !device.latitude || !device.longitude) return;
        const pos = { lat: device.latitude, lng: device.longitude };
        deviceMarkerRef.current = new window.google.maps.Marker({
            position: pos,
            map,
            title: deviceMapLabel(device),
        });
        map.panTo(pos);
        map.setZoom(13);
    }

    const activeInfoWindowRef = useRef(null);

    const getHeadingDirection = (heading) => {
        if (heading === undefined || heading === null) return "";
        const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
        const index = Math.round((heading % 360) / 45) % 8;
        return directions[index];
    };

    function createHistoryInfoWindow(data) {
        const isDeployed =
            data.deployment_status === 0 || data.deployment_status === "deployed";
        const isHalfDeployed =
            data.deployment_status === 4 ||
            data.deployment_status === "4" ||
            data.deployment_status === "half_deployed";

        let badgeOrText = '<span style="background: rgba(169, 169, 169, 0.75); color: white; padding: 4px 12px; border-radius: 12px; font-size: 0.85em; font-weight: 600;">⏸️ Not Deployed</span>';

        if (isDeployed) {
            badgeOrText = '<span style="background: rgba(5, 176, 8, 0.85); color: white; padding: 4px 12px; border-radius: 12px; font-size: 0.85em; font-weight: 600;">Deployed</span>';
        } else if (isHalfDeployed) {
            badgeOrText = '<span style="background: #f59e0b; color: white; padding: 4px 12px; border-radius: 12px; font-size: 0.85em; font-weight: 600;">⚠️ Half Deployed</span>';
        }

        const dtStr = new Date(parseAspDate(data.created_time)).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
        });

        const content = `
      <div style="padding: 12px; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; min-width: 280px; color: #333;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <div style="font-size: 1.1em; font-weight: 600; color: #242424;">Position Data</div>
          ${badgeOrText}
        </div>

        <div style="background: rgba(242, 105, 37, 0.75); border-radius: 12px; padding: 16px; margin-bottom: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
            <div style="text-align: center;">
              <div style="color: rgba(255,255,255,0.95); font-size: 0.75em; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Speed</div>
              <div style="color: white; font-size: 1.5em; font-weight: 700;">${data.speed !== undefined && data.speed !== null ? Number(data.speed).toFixed(0) : "—"}</div>
              <div style="color: rgba(255,255,255,0.85); font-size: 0.7em;">mph</div>
            </div>
            <div style="text-align: center;">
              <div style="color: rgba(255,255,255,0.95); font-size: 0.75em; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Heading</div>
              <div style="color: white; font-size: 1.5em; font-weight: 700;">${data.heading !== undefined && data.heading !== null ? Number(data.heading).toFixed(0) : "—"}°</div>
              <div style="color: rgba(255,255,255,0.85); font-size: 0.7em;">${getHeadingDirection(data.heading)}</div>
            </div>
          </div>
        </div>

        <div style="color: #777777; font-size: 0.85em; margin-top: 8px;">
          <div style="margin-bottom: 4px;">
            <span style="font-weight: 600;">Lat:</span> ${Number(data.latitude).toFixed(6)}
          </div>
          <div style="margin-bottom: 4px;">
            <span style="font-weight: 600;">Lng:</span> ${Number(data.longitude).toFixed(6)}
          </div>
          <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e5e5;">
             ${dtStr}
          </div>
        </div>
      </div>
    `;
        return new window.google.maps.InfoWindow({ content: content, closeBoxURL: "" });
    }

    function drawRoute(points) {
        const map = mapInstanceRef.current;
        if (!map) return;

        setDayRouteIsGpsDrift(false);

        if (!points.length) {
            map.setZoom(5);
            map.panTo({ lat: 39.5, lng: -98.35 });
            return;
        }

        const drift = detectGpsDriftCluster(points);
        if (drift) {
            window.reportPolylines = window.reportPolylines || [];

            const radiusMeters = Math.max(drift.radiusMiles * 1609.34, 40);
            const circle = new window.google.maps.Circle({
                strokeColor: drift.stroke,
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: drift.stroke,
                fillOpacity: 0.12,
                map,
                center: drift.center,
                radius: radiusMeters,
                zIndex: 1,
            });
            routeCirclesRef.current.push(circle);

            const centerMarker = new window.google.maps.Marker({
                position: drift.center,
                map,
                title: 'Approximate location (GPS drift — path shown as radius)',
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillOpacity: 0.95,
                    fillColor: drift.stroke,
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#ffffff',
                    scale: 7,
                },
                zIndex: 2,
            });
            centerMarker.addListener('click', () => {
                if (activeInfoWindowRef.current) activeInfoWindowRef.current.close();
                const sample = drift.samplePoint;
                if (sample) {
                    const infoWindow = createHistoryInfoWindow(sample);
                    infoWindow.open(map, centerMarker);
                    activeInfoWindowRef.current = infoWindow;
                }
            });
            routeMarkersRef.current.push(centerMarker);

            const cb = circle.getBounds();
            if (cb) map.fitBounds(cb, { top: 56, right: 48, bottom: 48, left: 48 });
            else {
                map.panTo(drift.center);
                map.setZoom(17);
            }
            setDayRouteIsGpsDrift(true);
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        window.reportPolylines = window.reportPolylines || [];

        points.forEach((history, index) => {
            if (!history.latitude || !history.longitude) return;

            let color = "#6b7280"; // Gray
            switch (history.deployment_status) {
                case "deployed": case 0: case "0":
                    history.deployment_status = "deployed";
                    color = "#10b981"; // Green
                    break;
                case "not_deployed": case 1: case "1":
                    history.deployment_status = "not_deployed";
                    color = "#6b7280";
                    break;
                case 4: case "4": case "half_deployed":
                    history.deployment_status = "half_deployed";
                    color = "#f59e0b"; // Orange
                    break;
                default:
                    history.deployment_status = "not_deployed";
            }

            let markerIcon;
            if (history.heading !== undefined && history.heading !== null) {
                markerIcon = {
                    path: "M 0,-2 L 1.5,2 L 0,1 L -1.5,2 Z",
                    fillColor: color,
                    fillOpacity: 0.9,
                    strokeColor: "#ffffff",
                    strokeWeight: 1,
                    scale: 4,
                    rotation: history.heading,
                    anchor: new window.google.maps.Point(0, 0),
                };
            } else {
                markerIcon = {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillOpacity: 0.7,
                    fillColor: color,
                    strokeOpacity: 0.9,
                    strokeWeight: 2,
                    strokeColor: "#ffffff",
                    scale: 6,
                };
            }

            const marker = new window.google.maps.Marker({
                position: { lat: history.latitude, lng: history.longitude },
                map: map,
                icon: markerIcon,
                zIndex: index,
            });

            marker.addListener("click", () => {
                if (activeInfoWindowRef.current) activeInfoWindowRef.current.close();
                const infoWindow = createHistoryInfoWindow(history);
                infoWindow.open(map, marker);
                activeInfoWindowRef.current = infoWindow;
            });

            routeMarkersRef.current.push(marker);
            bounds.extend(marker.getPosition());
        });

        // DRAW LINES BETWEEN POINTS
        for (let i = 1; i < points.length; i++) {
            const prev = points[i - 1];
            const curr = points[i];
            if (!prev.latitude || !prev.longitude || !curr.latitude || !curr.longitude) continue;

            const isPrevDeployed = prev.deployment_status === 0 || prev.deployment_status === "deployed";
            const isCurrDeployed = curr.deployment_status === 0 || curr.deployment_status === "deployed";
            const isPrevHalf = prev.deployment_status === 4 || prev.deployment_status === "half_deployed";
            const isCurrHalf = curr.deployment_status === 4 || curr.deployment_status === "half_deployed";

            let color = "#6b7280";
            if (isPrevDeployed && isCurrDeployed) color = "#10b981";
            else if (isPrevHalf || isCurrHalf) color = "#f59e0b";

            const polyline = new window.google.maps.Polyline({
                path: [
                    { lat: prev.latitude, lng: prev.longitude },
                    { lat: curr.latitude, lng: curr.longitude }
                ],
                geodesic: true,
                strokeColor: color,
                strokeOpacity: 0.8,
                strokeWeight: 3,
                zIndex: 1,
            });
            polyline.setMap(map);
            window.reportPolylines.push(polyline);
        }

        map.fitBounds(bounds);
    }

    // ── Handlers ─────────────────────────────────────────────────────────
    function handleDeviceClick(id) {
        setFocusedDeviceId(id);
        setFocusedDay(null);
    }

    function handleDayClick(deviceId, day, e) {
        e.stopPropagation();
        if (focusedDeviceId === deviceId && focusedDay === day) {
            setFocusedDay(null); // toggle off
        } else {
            setFocusedDeviceId(deviceId);
            setFocusedDay(day);
        }
    }

    // ── Derived summary values ────────────────────────────────────────────
    const workingCount = devices.filter(isWorking).length;
    const totalMilesAll = Object.values(deviceStats).reduce((s, x) => s + x.totalMiles, 0);
    const anyLoading = Object.values(deviceStats).some(x => x.loading);
    const focusedDevice = devices.find(d => d.id === focusedDeviceId);

    const headerNode = document.getElementById('tma-report-header-portal');
    const headerContent = (
        <div className={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <button className={styles.backBtn} onClick={onBack}>← Back</button>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                    <div className={styles.headerTitle}>
                        TMA Report — {customer?.name}
                    </div>
                    <div className={styles.headerSub}>
                        {fmtDateLabel(fromDate)} – {fmtDateLabel(toDate)}
                    </div>
                </div>
            </div>

            {/* ── Summary cards inside header ── */}
            <div className={styles.summaryCards}>
                <div className={styles.card}>
                    <span className={styles.cardValue}>{devices.length}</span>
                    <span className={styles.cardLabel}>Devices selected</span>
                </div>
                <div className={styles.card}>
                    <span className={`${styles.cardValue} ${workingCount > 0 ? styles.cardValueGreen : styles.cardValueGray}`}>
                        {workingCount} / {devices.length}
                    </span>
                    <span className={styles.cardLabel}>Working (last 24h)</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardValue}>
                        {anyLoading ? '…' : fmtMiles(totalMilesAll)}
                    </span>
                    <span className={styles.cardLabel}>Total distance</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.wrap}>
            {headerNode ? createPortal(headerContent, headerNode) : headerContent}

            {/* ── Main split layout ── */}
            <div className={styles.mainLayout}>

                {/* Left sidebar: device list */}
                <div className={styles.sidebar}>
                    {devices.map(device => {
                        const stats = deviceStats[device.id];
                        const working = isWorking(device);
                        const active = focusedDeviceId === device.id;
                        const { primary, secondary } = deviceCardTitles(device);

                        return (
                            <div
                                key={device.id}
                                className={`${styles.deviceCard} ${active ? styles.deviceCardActive : ''}`}
                                onClick={() => handleDeviceClick(device.id)}
                            >
                                <div className={styles.deviceCardHeader}>
                                    <div className={styles.deviceCardTitleBlock}>
                                        <span className={styles.deviceCardName}>
                                            {primary}
                                        </span>
                                        {secondary && secondary !== primary ? (
                                            <span className={styles.deviceCardSerial}>
                                                {secondary}
                                            </span>
                                        ) : null}
                                    </div>
                                    <span className={working ? styles.pill : styles.pillOff}>
                                        {working ? 'Working' : 'Offline'}
                                    </span>
                                </div>
                                <div className={styles.deviceRowDetails}>
                                    <span className={styles.deviceTotalMilesInline}>
                                        {fmtMiles(stats.totalMiles)} total miles
                                    </span>
                                </div>



                                {stats.loading ? (
                                    <div className={styles.loadingText}>Loading history…</div>
                                ) : (
                                    <>

                                        <div className={styles.dayGrid}>
                                            {stats.dailyMiles.map(({ date, miles, hasLocations }) => {
                                                const isActive = focusedDay === date && active;
                                                return (
                                                    <button
                                                        key={date}
                                                        className={`${styles.dayBtn} ${isActive ? styles.dayBtnActive : ''} ${miles > 0 || hasLocations ? styles.dayBtnMoved : ''}`}
                                                        onClick={e => handleDayClick(device.id, date, e)}
                                                        title={`${fmtDateLabel(date)}: ${fmtMiles(miles)}`}
                                                    >
                                                        <span className={styles.dayBtnWeekday}>{fmtWeekday(date)}</span>
                                                        <span className={styles.dayBtnDate}>{fmtDateShort(date)}</span>
                                                        <span className={styles.dayBtnMiles}>{fmtMiles(miles)}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Right: map */}
                <div className={styles.mapArea}>
                    {focusedDay && (
                        <div className={styles.mapLabel}>
                            <span>
                                {deviceMapLabel(focusedDevice)}
                                {' · '}
                                {fmtDateLabel(focusedDay)}
                                {!dayRouteLoading && (() => {
                                    const s = deviceStats[focusedDeviceId];
                                    const entry = s?.dailyMiles.find(x => x.date === focusedDay);
                                    return entry ? `  ·  ${fmtMiles(entry.miles)}` : '';
                                })()}
                                {!dayRouteLoading && dayRouteIsGpsDrift ? (
                                    <>
                                        {' · '}
                                        <span className={styles.mapDriftHint}>Stationary (GPS drift)</span>
                                    </>
                                ) : null}
                            </span>
                            <button className={styles.mapLabelClose} onClick={() => setFocusedDay(null)}>✕</button>
                        </div>
                    )}

                    {dayRouteLoading && (
                        <div className={styles.mapOverlay}>
                            <div className={styles.mapOverlayText}>Loading route…</div>
                        </div>
                    )}

                    <div ref={mapRef} className={styles.map} />
                </div>
            </div>
        </div>
    );
}
